import { type ChangeEvent, type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { buildIncomingFlow } from './incomingFlow';
import { FileText, FileUp, Plus, TriangleAlert } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import {
  DataTable,
  type DataTableColumn,
  FlowTrail,
  MetricTile,
  RecordField,
  RecordPanel,
  StatusPill,
  type StatusTone,
  SearchSelect,
  type SearchSelectOption,
  Tabs,
  type TabItem,
  CustomizableMetrics,
  WorkspacePage,
  confirmDiscard,
  useContextMenu,
  type ContextMenuItem,
} from '@forq/ui';
import { chartOfAccounts } from '@forq/finance-data';
import { FinanceNav } from '../FinanceNav';
import {
  incomingFlowTransitions,
  incomingStatusLabels,
  parseEInvoiceXml,
  reviewerRoleLabels,
  type IncomingInvoice,
  type IncomingInvoiceGateway,
  type IncomingInvoiceStatus,
  type IncomingTaxRate,
  type ParsedEInvoice,
  type ReviewerRole,
} from '@forq/finance-data';

interface IncomingInvoicesPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly gateway: IncomingInvoiceGateway;
}

type IncomingView = 'all' | 'capture' | 'review' | 'payment' | 'done';

const statusTone: Record<IncomingInvoiceStatus, StatusTone> = {
  captured: 'neutral', pre_booked: 'info', review_requested: 'warning', approved: 'info',
  payment_requested: 'warning', payment_released: 'progress', paid: 'done', rejected: 'danger', cancelled: 'danger',
};

function money(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}
function dateLabel(value: string | null): string {
  return value ? new Intl.DateTimeFormat('de-DE').format(new Date(`${value}T00:00:00`)) : '–';
}
function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Die Eingangsrechnung konnte nicht verarbeitet werden.';
}

function matchesView(invoice: IncomingInvoice, view: IncomingView): boolean {
  if (view === 'all') return true;
  if (view === 'capture') return invoice.status === 'captured' || invoice.status === 'pre_booked';
  if (view === 'review') return invoice.status === 'review_requested';
  if (view === 'payment') return invoice.status === 'payment_requested' || invoice.status === 'payment_released';
  return invoice.status === 'paid' || invoice.status === 'rejected' || invoice.status === 'cancelled';
}

const editableStatuses: readonly IncomingInvoiceStatus[] = ['captured', 'pre_booked', 'rejected'];

/**
 * Every account from the customer's chart, searchable. Deliberately NOT filtered to GuV accounts:
 * buying a truck is an Eingangsrechnung on a balance-sheet account (350 LKW), and hiding those
 * would block a legitimate booking. The BWA then reports such a purchase as unassigned expense,
 * which is correct — an asset is not an operating cost.
 */
const accountOptions: readonly SearchSelectOption[] = chartOfAccounts.map((account) => ({
  value: account.number,
  label: `${account.number} · ${account.name}`,
  hint: account.kind === 'guv' ? 'GuV' : 'Bilanz',
}));

export function IncomingInvoicesPage({ tenant, mode, permissions, gateway }: IncomingInvoicesPageProps) {
  const [invoices, setInvoices] = useState<readonly IncomingInvoice[]>([]);
  const [view, setView] = useState<IncomingView>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [captureDraft, setCaptureDraft] = useState<ParsedEInvoice | null>(null);
  const [draftKey, setDraftKey] = useState(0);

  const canEdit = permissions.has('finance.incoming.edit');
  const canCapture = permissions.has('finance.incoming.capture') || canEdit;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await gateway.listIncomingInvoices({ tenantId: tenant.id });
      setInvoices(list);
      setSelectedId((current) => current && list.some((i) => i.id === current) ? current : list[0]?.id ?? null);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [gateway, tenant.id]);

  useEffect(() => { void load(); }, [load]);

  const selected = selectedId ? invoices.find((i) => i.id === selectedId) ?? null : null;

  const metrics = useMemo(() => ({
    review: invoices.filter((i) => i.status === 'review_requested').length,
    payment: invoices.filter((i) => i.status === 'payment_requested' || i.status === 'payment_released').length,
    capture: invoices.filter((i) => i.status === 'captured' || i.status === 'pre_booked').length,
    openSum: invoices.filter((i) => !['paid', 'cancelled', 'rejected'].includes(i.status)).reduce((s, i) => s + i.grossAmount, 0),
  }), [invoices]);

  const submitCapture = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const created = await gateway.createIncomingInvoice({
        tenantId: tenant.id,
        supplierNumber: String(form.get('supplierNumber') ?? '').trim(),
        supplierName: String(form.get('supplierName') ?? '').trim(),
        supplierInvoiceNumber: String(form.get('supplierInvoiceNumber') ?? '').trim(),
        invoiceDate: String(form.get('invoiceDate') ?? ''),
        netAmount: Number(String(form.get('netAmount') ?? '0').replace(',', '.')),
        taxRate: Number(form.get('taxRate') ?? 19) as IncomingTaxRate,
      });
      setCapturing(false);
      await load();
      setSelectedId(created.id);
    } catch (captureError) {
      setError(errorMessage(captureError));
    } finally {
      setBusy(false);
    }
  }, [gateway, load, tenant.id]);

  /** Liest eine hochgeladene XRechnung/ZUGFeRD-XML und befüllt das Erfassen-Formular vor. Das Formular
   *  wird über den draftKey neu gemountet, damit die defaultValues greifen (die interne
   *  Lieferantennummer steht nicht im XML und wird von Hand ergänzt). */
  const handleEInvoiceFile = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const result = parseEInvoiceXml(await file.text());
      if (!result.ok) { setError(result.error); return; }
      setError(null);
      setCaptureDraft(result.data);
      setDraftKey((key) => key + 1);
    } catch (readError) {
      setError(errorMessage(readError));
    }
  }, []);

  const saveAccounting = useCallback(async (invoice: IncomingInvoice, event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const updated = await gateway.updateAccounting({
        tenantId: tenant.id,
        id: invoice.id,
        accountNumber: String(form.get('accountNumber') ?? ''),
        costCenter: String(form.get('costCenter') ?? ''),
        bookingText: String(form.get('bookingText') ?? ''),
        reviewerRole: String(form.get('reviewerRole') ?? '') as ReviewerRole | '',
      });
      setInvoices((current) => current.map((i) => i.id === updated.id ? updated : i));
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setBusy(false);
    }
  }, [gateway, tenant.id]);

  const advance = useCallback(async (invoice: IncomingInvoice, toStatus: IncomingInvoiceStatus) => {
    const note = toStatus === 'rejected' ? window.prompt('Begründung der Ablehnung:')?.trim() : undefined;
    if (toStatus === 'rejected' && !note) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await gateway.transitionIncoming({ tenantId: tenant.id, id: invoice.id, toStatus, note });
      setInvoices((current) => current.map((i) => i.id === updated.id ? updated : i));
    } catch (transitionError) {
      setError(errorMessage(transitionError));
    } finally {
      setBusy(false);
    }
  }, [gateway, tenant.id]);

  const { open: openIncomingMenu, menu: incomingMenu } = useContextMenu();
  const rowMenuItems = useCallback((i: IncomingInvoice): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [{ label: 'Öffnen', icon: <FileText size={15} />, onSelect: () => setSelectedId(i.id) }];
    incomingFlowTransitions(i, permissions).forEach((t, index) => items.push({
      label: t.label, danger: t.tone === 'danger', separatorBefore: index === 0, disabled: busy, onSelect: () => void advance(i, t.toStatus),
    }));
    return items;
  }, [permissions, busy, advance]);

  const columns: readonly DataTableColumn<IncomingInvoice>[] = [
    {
      key: 'number', header: 'Beleg', width: 'minmax(0, 1.1fr)',
      render: (i) => <span className="forq-stack"><strong>{i.number}</strong><small>Lief.-Nr. {i.supplierInvoiceNumber}</small></span>,
    },
    { key: 'supplier', header: 'Lieferant', width: 'minmax(0, 1.3fr)', render: (i) => <span className="forq-stack"><strong>{i.supplierName}</strong><small>{i.supplierNumber}</small></span> },
    { key: 'gross', header: 'Brutto', width: '120px', align: 'end', render: (i) => <span className="forq-cell-num">{money(i.grossAmount)}</span> },
    { key: 'status', header: 'Status', render: (i) => <StatusPill label={incomingStatusLabels[i.status]} tone={statusTone[i.status]} /> },
    { key: 'due', header: 'Fällig', width: '110px', render: (i) => dateLabel(i.dueDate) },
  ];

  const viewTabs: readonly TabItem[] = [
    { id: 'all', label: 'Alle' },
    { id: 'capture', label: 'Erfassung', badge: metrics.capture || undefined },
    { id: 'review', label: 'Fachfreigabe', badge: metrics.review || undefined },
    { id: 'payment', label: 'Zahlung', badge: metrics.payment || undefined },
    { id: 'done', label: 'Erledigt' },
  ];

  const rows = invoices.filter((i) => matchesView(i, view));
  const transitions = selected ? incomingFlowTransitions(selected, permissions) : [];
  const canEditAccounting = !!selected && canEdit && editableStatuses.includes(selected.status);

  return (
    <WorkspacePage
      customizeKey="finance-incoming"
      eyebrow={`FORQ Finance · ${mode === 'demo' ? 'Demo' : 'Supabase'}`}
      title="Eingangsrechnungen"
      description="Lieferantenrechnungen erfassen, kontieren und rollenbasiert freigeben – bis zur Zahlung."
      actions={canCapture ? <button type="button" className="finance-actions__btn" onClick={() => { setError(null); setCaptureDraft(null); setCapturing(true); }}><Plus size={15} /> Rechnung erfassen</button> : undefined}
      metrics={(
        <CustomizableMetrics
          pageKey="finance-incoming"
          tiles={[
            { key: 'review', node: <MetricTile label="Zur Fachfreigabe" value={metrics.review} hint="warten auf Prüfung" tone={metrics.review > 0 ? 'warning' : 'default'} /> },
            { key: 'payment', node: <MetricTile label="Zur Zahlung" value={metrics.payment} hint="freizugeben / offen" tone="info" /> },
            { key: 'capture', node: <MetricTile label="In Erfassung" value={metrics.capture} hint="erfasst / vorkontiert" /> },
            { key: 'openvolume', node: <MetricTile label="Offenes Volumen" value={money(metrics.openSum)} hint="brutto, unbezahlt" /> },
          ]}
        />
      )}
      toolbar={<div className="finance-toolbar"><FinanceNav /><Tabs items={viewTabs} activeId={view} onSelect={(id) => setView(id as IncomingView)} ariaLabel="Eingangsrechnungsfilter" /></div>}
    >
      {error && !capturing && <div className="finance-page__error">{error}</div>}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(i) => i.id}
        onRowSelect={(i) => setSelectedId(i.id)}
        onRowContext={(event, i) => openIncomingMenu(event, rowMenuItems(i))}
        isRowSelected={(i) => selected?.id === i.id}
        loading={loading}
        loadingLabel="Eingangsrechnungen werden geladen …"
        emptyLabel="Keine Eingangsrechnungen in dieser Ansicht."
        ariaLabel="Eingangsrechnungsliste"
      />
      {incomingMenu}

      {selected && (
        <RecordPanel
          eyebrow={`Eingangsrechnung · ${reviewerRoleLabels[selected.reviewerRole as ReviewerRole] ?? 'keine Prüfrolle'}`}
          title={`${selected.number} · ${selected.supplierName}`}
          subtitle={`Lieferantenrechnung ${selected.supplierInvoiceNumber} vom ${dateLabel(selected.invoiceDate)}`}
          status={<StatusPill label={incomingStatusLabels[selected.status]} tone={statusTone[selected.status]} />}
        >
          <FlowTrail steps={buildIncomingFlow(selected)} ariaLabel={`Freigabestatus ${selected.number}`} />

          {transitions.length > 0 && (
            <div className="finance-actions">
              {transitions.map((t) => (
                <button key={t.toStatus} type="button" className={`finance-actions__btn${t.tone === 'danger' ? ' is-danger' : ''}`} disabled={busy} onClick={() => void advance(selected, t.toStatus)}>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          <div className="forq-record-fields">
            <RecordField label="Netto" value={money(selected.netAmount)} />
            <RecordField label={`USt. ${selected.taxRate} %`} value={money(selected.taxAmount)} />
            <RecordField label="Brutto" value={money(selected.grossAmount)} />
            <RecordField label="Fällig" value={dateLabel(selected.dueDate)} />
          </div>

          <form className="finance-kontierung" onSubmit={(event) => void saveAccounting(selected, event)}>
            <div className="finance-kontierung__grid">
              <label>Sachkonto
                {/* A free text field let "3401" (does not exist) reach the tax advisor. Only real accounts now. */}
                <SearchSelect
                  name="accountNumber"
                  defaultValue={selected.accountNumber}
                  disabled={!canEditAccounting}
                  placeholder="Konto suchen …"
                  options={accountOptions}
                />
              </label>
              <label>Kostenstelle<input name="costCenter" defaultValue={selected.costCenter} disabled={!canEditAccounting} /></label>
              <label>Buchungstext<input name="bookingText" defaultValue={selected.bookingText} disabled={!canEditAccounting} /></label>
              <label>Prüfrolle
                <SearchSelect name="reviewerRole" defaultValue={selected.reviewerRole} disabled={!canEditAccounting} placeholder="– wählen –" options={(Object.keys(reviewerRoleLabels) as ReviewerRole[]).map((role) => ({ value: role, label: reviewerRoleLabels[role] }))} />
              </label>
            </div>
            {canEditAccounting && <button type="submit" className="finance-actions__btn" disabled={busy}>Kontierung speichern</button>}
          </form>

          {selected.decisionNote && <div className="finance-kontierung__note"><strong>Vermerk:</strong> {selected.decisionNote}</div>}
        </RecordPanel>
      )}

      {capturing && (
        <div className="finance-modal" role="dialog" aria-modal="true" aria-label="Eingangsrechnung erfassen" onMouseDown={(e) => { if (e.target === e.currentTarget && confirmDiscard()) { setCapturing(false); setError(null); } }}>
          <form key={draftKey} className="finance-modal__panel" onSubmit={submitCapture}>
            <header><FileText size={16} /> <h2>Eingangsrechnung erfassen</h2></header>
            <label className="finance-einvoice-drop">
              <span><FileUp size={15} /> E-Rechnung einlesen (XRechnung / ZUGFeRD als XML)</span>
              <input type="file" accept=".xml,application/xml,text/xml" onChange={handleEInvoiceFile} />
              {captureDraft
                ? <small className="is-loaded">Aus {captureDraft.format === 'ubl' ? 'XRechnung' : 'ZUGFeRD'} übernommen – bitte prüfen und die Lieferantennummer ergänzen.</small>
                : <small>Lieferant, Rechnungsnummer, Datum, Nettobetrag und Steuersatz werden automatisch übernommen.</small>}
            </label>
            {/* Unschärfen beim Lesen NIE verschlucken -- vorher wurde ein unbekannter Steuersatz
                still auf 0/7/19 gerundet und nur der erste Satz gelesen. */}
            {captureDraft && captureDraft.warnings.length > 0 && (
              <div className="finance-einvoice-warning" role="alert">
                <TriangleAlert size={15} aria-hidden />
                <ul>{captureDraft.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
              </div>
            )}
            <div className="finance-modal__grid">
              <label>Lieferantennummer<input name="supplierNumber" required autoFocus /></label>
              <label>Lieferant<input name="supplierName" required defaultValue={captureDraft?.supplierName ?? ''} /></label>
              <label>Lieferanten-Rechnungsnr.<input name="supplierInvoiceNumber" required defaultValue={captureDraft?.supplierInvoiceNumber ?? ''} /></label>
              <label>Rechnungsdatum<input name="invoiceDate" type="date" required defaultValue={captureDraft?.invoiceDate ?? ''} /></label>
              <label>Nettobetrag<input name="netAmount" type="number" min="0" step="0.01" required defaultValue={captureDraft ? String(captureDraft.netAmount) : ''} /></label>
              <label>USt.-Satz
                <SearchSelect name="taxRate" defaultValue={String(captureDraft?.taxRate ?? 19)} options={[{ value: '19', label: '19 %' }, { value: '7', label: '7 %' }, { value: '0', label: '0 %' }]} />
              </label>
            </div>
            {error && <div className="finance-modal__error">{error}</div>}
            <div className="finance-modal__actions">
              <button type="button" className="finance-actions__btn" onClick={() => { setCapturing(false); setError(null); }}>Abbrechen</button>
              <button type="submit" className="finance-actions__btn is-pay" disabled={busy}>Erfassen</button>
            </div>
          </form>
        </div>
      )}
    </WorkspacePage>
  );
}
